// frontend/src/components/admin/ReportsManagement.jsx
import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import api from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import {
  Loader2,
  CheckCircle,
  XCircle,
  Gavel,
  Clock,
  User,
  Package,
  MessageCircle,
  Shield,
  Calendar,
  FileText,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const SANCTION_TYPES = [
  { value: "warning", label: "⚠️ Advertência", color: "yellow" },
  { value: "temporary_ban", label: "⏰ Suspensão Temporária", color: "orange" },
  { value: "permanent_ban", label: "🚫 Banimento Permanente", color: "red" },
];

export default function ReportsManagement() {
  
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedReport, setExpandedReport] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    reviewed: 0,
    dismissed: 0,
  });

  // Estado para el modal de sanción
  const [sanctionModal, setSanctionModal] = useState({
    open: false,
    reportId: null,
    sellerName: "",
    postTitle: "",
  });
  const [sanctionForm, setSanctionForm] = useState({
    type: "warning",
    reason: "",
    description: "",
    expires_at: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadReports();
    loadStats();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/reports");
      if (response.data.success) {
        setReports(response.data.data);
      }
    } catch (error) {
      console.error("Error loading reports:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar as denúncias.",
        confirmButtonColor: "#449879",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get("/admin/reports/stats");
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const handleReview = async (reportId) => {
    try {
      const response = await api.patch(`/admin/reports/${reportId}/review`);
      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Denúncia revisada!",
          timer: 1500,
          showConfirmButton: false,
          customClass: {
            popup: "rounded-2xl",
          },
        });
        loadReports();
        loadStats();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível revisar a denúncia.",
        confirmButtonColor: "#449879",
      });
    }
  };

  const handleDismiss = async (reportId) => {
    const result = await Swal.fire({
      title: "Descartar denúncia?",
      text: "Esta denúncia será marcada como descartada.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#6b7280",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Sim, descartar",
      cancelButtonText: "Cancelar",
      customClass: {
        popup: "rounded-2xl",
        confirmButton: "rounded-xl px-6 py-2.5",
        cancelButton: "rounded-xl px-6 py-2.5",
      },
    });

    if (result.isConfirmed) {
      try {
        const response = await api.patch(`/admin/reports/${reportId}/dismiss`);
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Denúncia descartada!",
            timer: 1500,
            showConfirmButton: false,
            customClass: {
              popup: "rounded-2xl",
            },
          });
          loadReports();
          loadStats();
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: "Não foi possível descartar a denúncia.",
          confirmButtonColor: "#449879",
        });
      }
    }
  };

  const openSanctionModal = (report) => {
    setSanctionModal({
      open: true,
      reportId: report.id,
      sellerName: report.post?.seller?.apelido || report.post?.seller?.name || "Vendedor",
      postTitle: report.post?.titulo || "Anúncio",
    });
    setSanctionForm({
      type: "warning",
      reason: "",
      description: "",
      expires_at: "",
    });
  };

  const handleApplySanction = async () => {
    if (!sanctionForm.reason.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Informe o motivo",
        text: "Por favor, informe o motivo da sanção.",
        confirmButtonColor: "#449879",
        customClass: {
          popup: "rounded-2xl",
          confirmButton: "rounded-xl px-6 py-2.5",
        },
      });
      return;
    }

    if (sanctionForm.type === "temporary_ban" && !sanctionForm.expires_at) {
      Swal.fire({
        icon: "warning",
        title: "Informe a data de expiração",
        text: "Para suspensão temporária, informe a data de expiração.",
        confirmButtonColor: "#449879",
        customClass: {
          popup: "rounded-2xl",
          confirmButton: "rounded-xl px-6 py-2.5",
        },
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/admin/reports/${sanctionModal.reportId}/sanction`, {
        sanction_type: sanctionForm.type,
        reason: sanctionForm.reason,
        description: sanctionForm.description,
        expires_at: sanctionForm.type === "temporary_ban" ? sanctionForm.expires_at : null,
      });

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Sanção aplicada!",
          text: "A sanção foi aplicada com sucesso ao usuário.",
          confirmButtonColor: "#449879",
          customClass: {
            popup: "rounded-2xl",
            confirmButton: "rounded-xl px-6 py-2.5",
          },
        });
        setSanctionModal({ open: false, reportId: null, sellerName: "", postTitle: "" });
        loadReports();
        loadStats();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: error.response?.data?.message || "Não foi possível aplicar a sanção.",
        confirmButtonColor: "#449879",
        customClass: {
          popup: "rounded-2xl",
          confirmButton: "rounded-xl px-6 py-2.5",
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleExpand = (reportId) => {
    setExpandedReport(expandedReport === reportId ? null : reportId);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            <Clock className="w-3 h-3" />
            Pendente
          </span>
        );
      case "reviewed":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3" />
            Revisada
          </span>
        );
      case "dismissed":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            <XCircle className="w-3 h-3" />
            Descartada
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#449879] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
          <p className="text-sm text-gray-500">Total</p>
        </div>
        <div className="bg-yellow-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          <p className="text-sm text-yellow-600">Pendentes</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{stats.reviewed}</p>
          <p className="text-sm text-green-600">Revisadas</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-gray-600">{stats.dismissed}</p>
          <p className="text-sm text-gray-500">Descartadas</p>
        </div>
      </div>

      {/* Lista de denuncias */}
      {reports.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-2xl">
          <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Nenhuma denúncia encontrada</p>
          <p className="text-sm text-gray-400 mt-1">Aguardando denúncias dos usuários.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((report) => (
            <div
              key={report.id}
              className={`bg-white rounded-2xl border overflow-hidden transition-all ${
                report.status === "pending" ? "border-yellow-200 bg-yellow-50/30" : "border-gray-200"
              }`}
            >
              {/* Header de la denuncia */}
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleExpand(report.id)}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-semibold text-gray-800 truncate">
                        {report.post?.titulo || "Anúncio removido"}
                      </h4>
                      {getStatusBadge(report.status)}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {report.user?.apelido || report.user?.name || "Usuário"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Package className="w-3 h-3" />
                        Vendedor: {report.post?.seller?.apelido || report.post?.seller?.name || "Desconhecido"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(report.created_at).toLocaleDateString("pt-BR")}
                      </span>
                    </div>
                    <p className="text-sm text-red-600 mt-1">
                      <strong>Motivo:</strong> {report.reason}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {report.status === "pending" && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openSanctionModal(report);
                          }}
                          className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-xl transition-colors"
                          title="Aplicar sanção"
                        >
                          <Gavel className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReview(report.id);
                          }}
                          className="bg-green-50 hover:bg-green-100 text-green-600 p-2 rounded-xl transition-colors"
                          title="Marcar como revisada"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDismiss(report.id);
                          }}
                          className="bg-gray-50 hover:bg-gray-100 text-gray-600 p-2 rounded-xl transition-colors"
                          title="Descartar"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <div className="shrink-0">
                      {expandedReport === report.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Detalles expandidos */}
              {expandedReport === report.id && (
                <div className="p-4 border-t border-gray-100 bg-gray-50/50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="text-sm font-semibold text-gray-700 mb-2">
                        <MessageCircle className="w-4 h-4 inline mr-1" />
                        Detalles de la denuncia
                      </h5>
                      <div className="space-y-2 text-sm">
                        <p>
                          <strong>Denunciante:</strong> {report.user?.name} ({report.user?.email})
                        </p>
                        <p>
                          <strong>Anúncio:</strong> {report.post?.titulo}
                        </p>
                        <p>
                          <strong>Vendedor:</strong> {report.post?.seller?.name || "Desconhecido"}
                        </p>
                        {report.description && (
                          <div className="mt-2 p-3 bg-white rounded-lg">
                            <p className="text-gray-600">{report.description}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <h5 className="text-sm font-semibold text-gray-700 mb-2">
                        <FileText className="w-4 h-4 inline mr-1" />
                        Información adicional
                      </h5>
                      <div className="space-y-2 text-sm">
                        <p>
                          <strong>Data:</strong> {new Date(report.created_at).toLocaleString("pt-BR")}
                        </p>
                        {report.admin_notes && (
                          <p>
                            <strong>Notas do admin:</strong> {report.admin_notes}
                          </p>
                        )}
                        <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                          <p className="text-xs text-blue-600">
                            💡 Ao aplicar uma sanção, o usuário será notificado e as medidas serão aplicadas automaticamente.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal de Sanción */}
      {sanctionModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <Gavel className="w-5 h-5 text-red-500" />
                Aplicar Sanção
              </h2>
              <button
                onClick={() => setSanctionModal({ open: false, reportId: null, sellerName: "", postTitle: "" })}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XCircle className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-sm text-gray-600">
                  <strong>Usuário:</strong> {sanctionModal.sellerName}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Anúncio:</strong> {sanctionModal.postTitle}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de sanção *
                </label>
                <select
                  value={sanctionForm.type}
                  onChange={(e) => setSanctionForm({ ...sanctionForm, type: e.target.value })}
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#449879] focus:border-transparent"
                >
                  {SANCTION_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {sanctionForm.type === "temporary_ban" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de expiração *
                  </label>
                  <input
                    type="datetime-local"
                    value={sanctionForm.expires_at}
                    onChange={(e) => setSanctionForm({ ...sanctionForm, expires_at: e.target.value })}
                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#449879] focus:border-transparent"
                    min={new Date().toISOString().slice(0, 16)}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo da sanção *
                </label>
                <input
                  type="text"
                  value={sanctionForm.reason}
                  onChange={(e) => setSanctionForm({ ...sanctionForm, reason: e.target.value })}
                  placeholder="Ex: Violação das normas da comunidade"
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#449879] focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição adicional
                </label>
                <textarea
                  value={sanctionForm.description}
                  onChange={(e) => setSanctionForm({ ...sanctionForm, description: e.target.value })}
                  placeholder="Detalhes adicionais sobre a sanção..."
                  rows="3"
                  className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-[#449879] focus:border-transparent resize-none"
                />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setSanctionModal({ open: false, reportId: null, sellerName: "", postTitle: "" })}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleApplySanction}
                  disabled={submitting || !sanctionForm.reason.trim()}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-xl font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Gavel className="w-4 h-4" />
                  )}
                  {submitting ? "Aplicando..." : "Aplicar Sanção"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.25s ease-out;
          }
        `}
      </style>
    </div>
  );
}